import { useCallback, useEffect, useRef } from "react";
import { createActiveTimeTracker } from "./activeTime";
import {
  emitProductAnalyticsEvent,
  isProductAnalyticsConfigured,
  PRODUCT_ANALYTICS_SECTIONS,
  startProductAnalytics,
} from "./productAnalytics";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];
const FLUSH_MS = 30 * 1000;

const validAudience = (audience) => audience === "staff" || audience === "customer";

export default function useProductAnalytics({ audience, section, suppressed = false }) {
  const previousRef = useRef(null);
  const latestRef = useRef({ audience: null, section: null });

  latestRef.current = { audience, section };

  const trackAction = useCallback((action) => {
    const context = latestRef.current;
    emitProductAnalyticsEvent("portal_action", { ...context, action });
  }, []);

  useEffect(() => {
    if (!isProductAnalyticsConfigured() || suppressed || !validAudience(audience)
        || !PRODUCT_ANALYTICS_SECTIONS.has(section)) {
      previousRef.current = null;
      return undefined;
    }

    startProductAnalytics();
    // Active time is duration, so use the browser's monotonic clock rather
    // than wall-clock time, which may move backwards or forwards.
    const now = () => performance.now();
    const tracker = createActiveTimeTracker(now(), { visible: !document.hidden });
    const current = { audience, section };
    const previous = previousRef.current;

    // Strict Mode repeats effects in development. The ref survives its effect
    // replay, so it cannot create duplicate section or navigation events.
    if (!previous || previous.audience !== audience || previous.section !== section) {
      emitProductAnalyticsEvent("portal_section_visit", current);
      if (previous && previous.audience === audience) {
        emitProductAnalyticsEvent("portal_navigation", {
          audience,
          fromSection: previous.section,
          toSection: section,
        });
      }
    }
    previousRef.current = current;

    const flush = (at = now()) => {
      const activeMs = tracker.flush(at);
      if (activeMs > 0) emitProductAnalyticsEvent("portal_active_time", { ...current, activeMs });
    };
    const onActivity = () => {
      tracker.activity(now());
    };
    const onVisibility = () => {
      const at = now();
      const activeMs = tracker.visibility(at, !document.hidden);
      if (activeMs > 0) emitProductAnalyticsEvent("portal_active_time", { ...current, activeMs });
    };
    const onPageHide = () => flush(now());

    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, onActivity, { passive: true, capture: event === "scroll" }));
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    const interval = window.setInterval(() => flush(now()), FLUSH_MS);

    return () => {
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity, event === "scroll"));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      const activeMs = tracker.stop(now());
      if (activeMs > 0) emitProductAnalyticsEvent("portal_active_time", { ...current, activeMs });
    };
  }, [audience, section, suppressed]);

  return { trackAction };
}
